import { useState, useRef, useEffect, useCallback } from 'react';
import { MessageCircle, X, Send, Loader2, Trash2, Sparkles, Maximize2, Minimize2, GripHorizontal } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import axios from 'axios';
import { useAuth } from '@/App';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function WingmanChat({ activeRecordSsot = null } = {}) {
  const { token, user } = useAuth();
  // ★ H6 — global bridge: host pages (Spreadsheet / Growth-vs-Model / Snapshot)
  //   publish their current SSOT to window.__pfd_active_record_ssot when a
  //   record is loaded. WingmanChat reads it on each send so answers stay
  //   bound to the on-screen analysis. The prop override above is a hook for
  //   future explicit wiring; today every host page uses the window bridge.
  const resolveActiveSsot = () => {
    if (activeRecordSsot) return activeRecordSsot;
    if (typeof window !== 'undefined' && window.__pfd_active_record_ssot) {
      return window.__pfd_active_record_ssot;
    }
    return null;
  };
  const isTrial = user?.is_demo || !user?.is_unlimited;
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const scrollRef = useRef(null);

  // ★ Record-aware suggested prompts — derived from window.__pfd_active_record_ssot.
  //   Host pages publish the SSOT directly to window, so we poll it here (600ms)
  //   and re-render whenever it changes. The poll is cheap — we only shallow-check
  //   four fields. When the panel is closed we short-circuit to zero work.
  const [recordSsot, setRecordSsot] = useState(() => resolveActiveSsot());
  useEffect(() => {
    if (!open) return undefined;
    const read = () => {
      const next = resolveActiveSsot();
      setRecordSsot((prev) => {
        // Shallow compare the handful of fields we drive prompts off — avoids
        // thrashing React re-renders when the host publishes identical SSOTs.
        const keys = ['record_name', 'confidenceLabel', 'missingFishRangeLabel', 'hasConflict', 'actionability'];
        const same = prev && next && keys.every((k) => prev[k] === next[k]);
        if (same) return prev;
        if (!prev && !next) return prev;
        return next;
      });
    };
    read();
    const t = setInterval(read, 600);
    return () => clearInterval(t);
  }, [open]);

  // Derive the SSOT-driven prompts (max 5, no duplicates, record-specific only).
  // Rules mirror PRD exactly — NO generic prompts mixed in. When no SSOT exists
  // we return an empty list and the chip row is hidden (see render below).
  const suggestedPrompts = (() => {
    if (!recordSsot) return [];
    const out = [];
    const seen = new Set();
    const push = (q) => {
      if (out.length >= 5) return;
      if (seen.has(q)) return;
      seen.add(q);
      out.push(q);
    };

    const label = recordSsot.missingFishRangeLabel;
    const hasMissingSignal = !!label && label !== 'Within margin';
    if (hasMissingSignal) {
      push('Why is this cage showing missing fish?');
      push('How confident is this missing fish signal?');
    }

    const cl = recordSsot.confidenceLabel;
    if (cl === 'Low' || cl === 'Indication Only' || cl === 'Uncertain') {
      push('What data is missing to improve confidence?');
    }

    if (recordSsot.hasConflict === true) {
      push('Why are the signals conflicting in this record?');
    }

    // Always-present farm-action anchor.
    push('What should I check next on the farm?');

    return out;
  })();
  const sendSuggestedPrompt = (q) => {
    if (!q || loading) return;
    setInput(q);
    sendMessage(q);
  };

  // ★ External trigger bridge — `WingmanTriggerPrompts` (mounted next to the
  //   "How to Read" guides on the analysis pages) dispatches a window-level
  //   CustomEvent `pfd:wingman:ask` with `{ text }`. We open the panel and
  //   auto-send the composed question so the user never has to click Send.
  //   Messages are queued (FIFO) so that a user spamming chips during a still-
  //   streaming AI reply has every question honoured — nothing is dropped.
  //   The queue drains whenever the panel is open and not currently loading.
  const askQueueRef = useRef([]);
  useEffect(() => {
    const onAsk = (ev) => {
      const text = ev?.detail?.text;
      if (!text || typeof text !== 'string') return;
      askQueueRef.current.push(text);
      setOpen(true);
    };
    window.addEventListener('pfd:wingman:ask', onAsk);
    return () => window.removeEventListener('pfd:wingman:ask', onAsk);
  }, []);
  // Drain the queue whenever we are open and idle. Runs once per state
  // transition (loading: true → false) so pending chip asks are sent in
  // order as soon as the previous response completes.
  useEffect(() => {
    if (!open) return undefined;
    if (loading) return undefined;
    if (askQueueRef.current.length === 0) return undefined;
    const next = askQueueRef.current.shift();
    // Small delay so the SSOT bridge poll inside the panel has a chance to
    // read the latest active record before the message is POSTed.
    const timer = setTimeout(() => { sendMessage(next); }, 60);
    return () => clearTimeout(timer);
    // eslint-disable-next-line
  }, [open, loading]);

  // Drag state
  const [position, setPosition] = useState({ x: null, y: null });
  const [isDragging, setIsDragging] = useState(false);
  const dragRef = useRef(null);
  const dragStartRef = useRef({ x: 0, y: 0, posX: 0, posY: 0 });

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  // Reset position when toggling expanded mode
  useEffect(() => {
    if (expanded) {
      setPosition({ x: null, y: null });
    }
  }, [expanded]);

  // Drag handlers
  const onDragStart = useCallback((e) => {
    if (expanded) return;
    e.preventDefault();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    const panel = dragRef.current;
    if (!panel) return;

    const rect = panel.getBoundingClientRect();
    dragStartRef.current = {
      x: clientX,
      y: clientY,
      posX: rect.left,
      posY: rect.top,
    };
    setIsDragging(true);
  }, [expanded]);

  useEffect(() => {
    if (!isDragging) return;

    const onMove = (e) => {
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      const dx = clientX - dragStartRef.current.x;
      const dy = clientY - dragStartRef.current.y;
      const newX = dragStartRef.current.posX + dx;
      const newY = dragStartRef.current.posY + dy;

      // Clamp to viewport
      const maxX = window.innerWidth - 100;
      const maxY = window.innerHeight - 60;
      setPosition({
        x: Math.max(0, Math.min(newX, maxX)),
        y: Math.max(0, Math.min(newY, maxY)),
      });
    };

    const onEnd = () => setIsDragging(false);

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onEnd);
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onEnd);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onEnd);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onEnd);
    };
  }, [isDragging]);

  const sendMessage = async (explicitText) => {
    // ★ Record-aware prompts pass the question text directly so users don't
    //   have to also click Send. When no arg is provided we fall back to the
    //   input field (normal typed message flow).
    const text = (typeof explicitText === 'string' && explicitText.trim())
      ? explicitText.trim()
      : input.trim();
    if (!text || loading) return;

    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: text }]);
    setLoading(true);

    try {
      // ★ H6 — forward the on-screen record's SSOT so Wingman answers
      //   stay locked to the numbers the user is actually looking at.
      //   Shape is defined by the host page (Spreadsheet / Growth-vs-Model /
      //   Snapshot). When omitted Wingman falls back to general-knowledge mode.
      const _ssot = resolveActiveSsot();
      const record_ssot = _ssot ? {
        record_name: _ssot.record_name || null,
        species: _ssot.species || null,
        confidenceLabel: _ssot.confidenceLabel || null,
        actionability: _ssot.actionability || null,
        missingFishRangeLabel: _ssot.missingFishRangeLabel || null,
        feedingState: _ssot.feedingState || null,
        hasConflict: !!_ssot.hasConflict,
        detectionType: _ssot.detectionType || null,
        hasAnomaly: !!_ssot.hasAnomaly,
        hasMissingFishSignal: !!_ssot.hasMissingFishSignal,
        moduleName: _ssot.moduleName || null,
        harvest_correction: _ssot.harvestCorrection || null,
      } : null;
      // ★ Structured decision payload — module-specific. For the Spreadsheet
      //   Uploader we forward pre-ranked causes/checks computed client-side
      //   so Wingman doesn't have to sort them loosely. Backend has a mirror
      //   fallback ranker if this is missing.
      const wingman_context = _ssot ? {
        moduleName: _ssot.moduleName || null,
        recordName: _ssot.record_name || null,
        confidenceLabel: _ssot.confidenceLabel || null,
        actionability: _ssot.actionability || null,
        detectionType: _ssot.detectionType || null,
        feedingState: _ssot.feedingState || null,
        missingFishRangeLabel: _ssot.missingFishRangeLabel || null,
        rankedCauses: Array.isArray(_ssot.rankedCauses) ? _ssot.rankedCauses : null,
        rankedChecks: Array.isArray(_ssot.rankedChecks) ? _ssot.rankedChecks : null,
        harvest_correction: _ssot.harvestCorrection || null,
      } : null;
      const res = await axios.post(`${API}/wingman/chat`, {
        message: text,
        session_id: sessionId,
        record_ssot,
        wingman_context,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!sessionId) setSessionId(res.data.session_id);
      setMessages(prev => [...prev, { role: 'assistant', content: res.data.response }]);
    } catch (err) {
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'Sorry, I encountered an error. Please try again.' 
      }]);
    } finally {
      setLoading(false);
    }
  };

  const newChat = () => {
    setMessages([]);
    setSessionId('');
  };

  if (!token || !user || user.is_restricted_demo) return null;

  // Panel sizing
  const panelClass = expanded
    ? 'fixed inset-4 z-50'
    : 'fixed z-50';

  const panelStyle = expanded
    ? {}
    : position.x != null
      ? { left: position.x, top: position.y, width: 380, height: 520 }
      : { bottom: 96, right: 20, width: 380, height: 520 };

  return (
    <>
      <style>{`
        @keyframes bounce-slow {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-4px); }
        }
        .animate-bounce-slow { animation: bounce-slow 2s ease-in-out infinite; }
      `}</style>
      {/* Floating button */}
      {!open && (
        <div className="fixed bottom-24 right-5 z-50 flex flex-col items-end gap-2">
          <div className="bg-slate-800 text-white text-xs px-3 py-1.5 rounded-full shadow-md animate-bounce-slow whitespace-nowrap flex items-center gap-1.5">
            {isTrial && <Sparkles className="h-3 w-3 text-amber-400" />}
            {isTrial ? 'Premium: Ask Wingman anything' : 'Ask Wingman anything'}
          </div>
          <button
            data-testid="wingman-toggle"
            onClick={() => setOpen(true)}
            className="relative group bg-gradient-to-br from-[#0891b2] to-[#065a73] hover:from-[#06748c] hover:to-[#054d61] text-white rounded-2xl p-3.5 shadow-xl transition-all hover:scale-105 hover:shadow-2xl"
            title="PFD Wingman — Your AI Fish Expert"
          >
            <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-400 rounded-full border-2 border-white animate-pulse" />
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="h-7 w-7">
              <path d="M12 2C6.48 2 2 5.58 2 10c0 2.24 1.12 4.26 2.92 5.7L3 22l4.35-2.18C8.78 20.27 10.35 20.5 12 20.5c5.52 0 10-3.08 10-7.25S17.52 2 12 2z" />
              <circle cx="8.5" cy="10" r="0.8" fill="currentColor" />
              <circle cx="12" cy="10" r="0.8" fill="currentColor" />
              <circle cx="15.5" cy="10" r="0.8" fill="currentColor" />
            </svg>
          </button>
        </div>
      )}

      {/* Chat panel */}
      {open && (
        <div
          ref={dragRef}
          data-testid="wingman-panel"
          className={`${panelClass} bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden`}
          style={panelStyle}
        >
          {/* Header — draggable */}
          <div
            className={`bg-gradient-to-r from-[#0891b2] to-[#065a73] px-4 py-3 flex items-center justify-between shrink-0 ${!expanded ? 'cursor-grab active:cursor-grabbing' : ''} select-none`}
            onMouseDown={onDragStart}
            onTouchStart={onDragStart}
          >
            <div className="flex items-center gap-2.5">
              {!expanded && (
                <GripHorizontal className="h-4 w-4 text-cyan-300/60 shrink-0" />
              )}
              <div className="bg-white/15 rounded-lg p-1.5">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5 text-white">
                  <path d="M12 2C6.48 2 2 5.58 2 10c0 2.24 1.12 4.26 2.92 5.7L3 22l4.35-2.18C8.78 20.27 10.35 20.5 12 20.5c5.52 0 10-3.08 10-7.25S17.52 2 12 2z" />
                  <circle cx="8.5" cy="10" r="0.8" fill="currentColor" />
                  <circle cx="12" cy="10" r="0.8" fill="currentColor" />
                  <circle cx="15.5" cy="10" r="0.8" fill="currentColor" />
                </svg>
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <p className="text-white text-sm font-semibold tracking-wide">PFD Wingman</p>
                  {isTrial && (
                    <span className="inline-flex items-center gap-1 bg-amber-400/90 text-amber-950 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full" data-testid="wingman-chat-premium-badge">
                      <Sparkles className="h-2.5 w-2.5" />
                      Premium
                    </span>
                  )}
                </div>
                <p className="text-cyan-200 text-[11px] flex items-center gap-1">
                  <span className="inline-block w-1.5 h-1.5 bg-green-400 rounded-full"></span>
                  AI Expert — GPT&#8209;5.2
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={newChat} className="text-cyan-100 hover:text-white p-1" title="New chat">
                <Trash2 className="h-4 w-4" />
              </button>
              <button
                data-testid="wingman-expand-toggle"
                onClick={() => { setExpanded(!expanded); }}
                className="text-cyan-100 hover:text-white p-1"
                title={expanded ? 'Minimize' : 'Expand'}
              >
                {expanded ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
              </button>
              <button onClick={() => { setOpen(false); setExpanded(false); setPosition({ x: null, y: null }); }} className="text-cyan-100 hover:text-white p-1">
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-3 bg-slate-50">
            {messages.length === 0 && (
              <div className="text-center py-6">
                <div className="w-14 h-14 bg-gradient-to-br from-[#0891b2]/10 to-[#065a73]/10 rounded-2xl flex items-center justify-center mx-auto mb-3">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="h-7 w-7 text-[#0891b2]">
                    <path d="M12 2C6.48 2 2 5.58 2 10c0 2.24 1.12 4.26 2.92 5.7L3 22l4.35-2.18C8.78 20.27 10.35 20.5 12 20.5c5.52 0 10-3.08 10-7.25S17.52 2 12 2z" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <p className="text-sm font-medium text-slate-700">Hi{user?.name ? `, ${user.name.split(' ')[0]}` : ''}!</p>
                <p className="text-xs text-slate-400 mt-1 leading-relaxed px-4">I'm your AI expert for everything PFD — ask about analysis features, interpreting results, or aquaculture concepts.</p>
                {isTrial && (
                  <div className="mt-2 mx-4 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="wingman-trial-notice">
                    <p className="text-[11px] text-amber-700 font-medium flex items-center justify-center gap-1">
                      <Sparkles className="h-3 w-3 text-amber-500" />
                      You're using a premium feature — upgrade for unlimited access
                    </p>
                  </div>
                )}
                <div className="mt-4 space-y-1.5 px-2">
                  {/* ★ Record-aware empty-state chips: drive off the SSOT when
                      present, fall back to the static educational prompts
                      otherwise. Max 5, no duplicates. */}
                  {(suggestedPrompts.length > 0 ? suggestedPrompts : ['What is FCR?', 'How do I detect missing fish?', 'Explain the green line on my chart']).map(q => (
                    <button
                      key={q}
                      onClick={() => sendSuggestedPrompt(q)}
                      className="block w-full text-left text-xs bg-white border border-slate-200 rounded-lg px-3 py-2 text-slate-600 hover:border-[#0891b2] hover:text-[#0891b2] transition-colors"
                      data-testid={`wingman-suggested-empty-${q.slice(0, 20).replace(/\s+/g, '-').toLowerCase()}`}
                    >
                      {q}
                    </button>
                  ))}
                </div>

                <div className="mt-5 mx-2 bg-gradient-to-br from-teal-50 to-emerald-50 border border-teal-200 rounded-xl p-4 text-left" data-testid="wingman-benefits-section">
                  <p className="text-xs font-bold text-teal-800 mb-3 uppercase tracking-wider">What Wingman Can Do For You</p>
                  
                  <div className="space-y-2.5">
                    <div className="flex items-start gap-2.5">
                      <div className="mt-0.5 w-5 h-5 rounded-full bg-teal-500 flex items-center justify-center shrink-0">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-3 h-3"><path d="M12 2C6.48 2 2 5.58 2 10c0 2.24 1.12 4.26 2.92 5.7L3 22l4.35-2.18C8.78 20.27 10.35 20.5 12 20.5c5.52 0 10-3.08 10-7.25S17.52 2 12 2z" /></svg>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-teal-900">Journal-Backed Knowledge</p>
                        <p className="text-[11px] text-teal-700 leading-relaxed">Trained on aquaculture journals — species-specific FCR benchmarks, SGR formulas, feeding rates, mortality norms, stocking density effects, and cage culture best practices</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-2.5">
                      <div className="mt-0.5 w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center shrink-0">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-3 h-3"><path d="M9 18l6-6-6-6" /></svg>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-teal-900">One-Click Analysis Insights</p>
                        <p className="text-[11px] text-teal-700 leading-relaxed">After running any analysis, tap "Ask Wingman for AI Insight" — get a decision-focused summary comparing your results against industry benchmarks</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-2.5">
                      <div className="mt-0.5 w-5 h-5 rounded-full bg-amber-500 flex items-center justify-center shrink-0">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-3 h-3"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-teal-900">Proactive Alerts</p>
                        <p className="text-[11px] text-teal-700 leading-relaxed">Wingman automatically scans your results for red flags — missing fish, overfeeding, trajectory jumps — and alerts you before you even ask</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-2.5">
                      <div className="mt-0.5 w-5 h-5 rounded-full bg-indigo-500 flex items-center justify-center shrink-0">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-3 h-3"><circle cx="12" cy="12" r="10" /><path d="M12 16v-4" /><path d="M12 8h.01" /></svg>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-teal-900">Your Data + Science Combined</p>
                        <p className="text-[11px] text-teal-700 leading-relaxed">Wingman can reference the report you&rsquo;re viewing and cross-reference it against aquaculture science — Sea Bream FCR norms (1.2–1.8), optimal temperature ranges (20–25°C), mortality thresholds, and more. Open a saved analysis first for record-aware answers.</p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-3 pt-3 border-t border-teal-200">
                    <p className="text-[10px] text-teal-600 font-medium">Powered by GPT-5.2 + aquaculture journals — available on every analysis page</p>
                  </div>
                </div>
              </div>
            )}

            {messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] rounded-xl px-3.5 py-2.5 text-sm leading-relaxed ${
                  msg.role === 'user'
                    ? 'bg-[#0891b2] text-white rounded-br-sm'
                    : 'bg-white border border-slate-200 text-slate-800 rounded-bl-sm shadow-sm'
                }`}>
                  {msg.role === 'assistant' ? (
                    <div className="prose prose-sm prose-slate max-w-none [&>p]:mb-2 [&>ul]:mb-2 [&>ol]:mb-2 [&>p:last-child]:mb-0 [&>ul:last-child]:mb-0">
                      <ReactMarkdown>{msg.content}</ReactMarkdown>
                    </div>
                  ) : msg.content}
                </div>
              </div>
            ))}

            {loading && (
              <div className="flex justify-start">
                <div className="bg-white border border-slate-200 rounded-xl rounded-bl-sm px-3.5 py-2.5 shadow-sm flex items-center gap-2">
                  <Loader2 className="h-3.5 w-3.5 text-[#0891b2] animate-spin" />
                  <span className="text-xs text-slate-500">Thinking...</span>
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <div className="p-3 border-t border-slate-200 bg-white shrink-0">
            {/* ★ Record-aware suggested prompts — always above the input so the
                user can trigger them throughout the conversation. Hidden entirely
                when no SSOT is present (component must not render generic copy). */}
            {suggestedPrompts.length > 0 && (
              <div className="mb-2 flex flex-wrap gap-1.5" data-testid="wingman-suggested-row">
                {suggestedPrompts.map((q) => (
                  <button
                    key={q}
                    type="button"
                    onClick={() => sendSuggestedPrompt(q)}
                    disabled={loading}
                    className="text-[11px] bg-slate-50 border border-slate-200 text-slate-700 rounded-full px-2.5 py-1 hover:border-[#0891b2] hover:text-[#0891b2] hover:bg-[#0891b2]/5 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    data-testid={`wingman-suggested-chip-${q.slice(0, 24).replace(/\s+/g, '-').toLowerCase()}`}
                    title={q}
                  >
                    {q}
                  </button>
                ))}
              </div>
            )}
            <div className="flex gap-2">
              <input
                data-testid="wingman-input"
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
                placeholder="Ask anything about PFD..."
                className="flex-1 border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0891b2] focus:border-transparent placeholder:text-slate-400"
                disabled={loading}
              />
              <button
                data-testid="wingman-send"
                onClick={sendMessage}
                disabled={loading || !input.trim()}
                className="bg-[#0891b2] hover:bg-[#06748c] disabled:bg-slate-300 text-white rounded-xl p-2.5 transition-colors"
              >
                <Send className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
